import { useTranslation } from '@thoughtspot/i18n';
import { Modal, ModalSize } from '@thoughtspot/radiant-react/widgets/modal';
import { WizardModal, } from '@thoughtspot/radiant-react/widgets/wizard-modal';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { V2TranslationKeys } from '../../constants';
import createWizardStyles from '../create-user-wizard/create-user-wizard.module.scss';
import { StepAssignGroups } from '../create-user-wizard/step-assign-groups';
import { StepCredentials } from '../create-user-wizard/step-credentials';
import styles from './edit-user-wizard.module.scss';
import { useEditUserForm } from './useEditUserForm';
import { SubmitResult } from './utils';
export const EditUserWizard = ({ isOpen, userId, isClusterAdmin, onClose, onSuccess, }) => {
    const { t } = useTranslation();
    const [currentStep, setCurrentStep] = useState(0);
    const [selectedGroupIds, setSelectedGroupIds] = useState(new Set());
    const [selectedOrgIds, setSelectedOrgIds] = useState(new Set());
    const { form, actions, errors, isLoading, isSubmitting, validateCredentials, handleSubmit, originalValues, groupsData, orgsData, } = useEditUserForm({ userId, isClusterAdmin });
    useEffect(() => {
        if (isOpen && originalValues) {
            setSelectedGroupIds(originalValues.groupIds);
            setSelectedOrgIds(originalValues.orgIds);
        }
    }, [isOpen, originalValues]);
    useEffect(() => {
        if (isOpen)
            setCurrentStep(0);
    }, [isOpen]);
    const groupItems = useMemo(() => {
        const items = groupsData.list.map(group => ({
            id: group.id,
            label: group.name,
        }));
        if (!originalValues)
            return items;
        return items.sort((itemA, itemB) => {
            const pinA = originalValues.groupIds.has(itemA.id) ? 0 : 1;
            const pinB = originalValues.groupIds.has(itemB.id) ? 0 : 1;
            return pinA - pinB;
        });
    }, [groupsData.list, originalValues]);
    const orgItems = useMemo(() => {
        const items = orgsData.list.map(org => ({
            id: org.orgId,
            label: org.orgName,
        }));
        if (!originalValues)
            return items;
        return items.sort((itemA, itemB) => {
            const pinA = originalValues.orgIds.has(itemA.id) ? 0 : 1;
            const pinB = originalValues.orgIds.has(itemB.id) ? 0 : 1;
            return pinA - pinB;
        });
    }, [orgsData.list, originalValues]);
    const handleFinalSubmit = useCallback(async () => {
        if (isClusterAdmin && !validateCredentials()) {
            setCurrentStep(0);
            return;
        }
        const result = await handleSubmit(selectedGroupIds, selectedOrgIds);
        if (result === SubmitResult.SUCCESS)
            onSuccess();
        if (result === SubmitResult.NO_CHANGES)
            onClose();
    }, [
        isClusterAdmin,
        validateCredentials,
        handleSubmit,
        selectedGroupIds,
        selectedOrgIds,
        onSuccess,
        onClose,
    ]);
    if (isClusterAdmin) {
        const wizardSteps = [
            {
                stepTitle: t(V2TranslationKeys.EDIT_CREDENTIALS_STEP),
                stepContent: (React.createElement(StepCredentials, { form: form, actions: actions, errors: errors, hidePasswordFields: true, contentTitle: t('users.wizard.contentTitle.editUserCredentials') })),
                stepForwardButton: {
                    text: t('modal.next'),
                    onNext: () => {
                        if (validateCredentials())
                            setCurrentStep(1);
                    },
                    isDisabled: isSubmitting || isLoading,
                },
            },
            {
                stepTitle: t('users.wizard.stepTitle.assignOrgs'),
                stepContent: (React.createElement(StepAssignGroups, { groups: groupItems, groupsLoading: groupsData.loading, selectedGroupIds: selectedGroupIds, onGroupSelectionChange: setSelectedGroupIds, groupSearchText: groupsData.searchText, onGroupSearchChange: groupsData.setSearchText, orgs: orgItems, orgsLoading: orgsData.loading, selectedOrgIds: selectedOrgIds, onOrgSelectionChange: setSelectedOrgIds, isClusterAdmin: isClusterAdmin })),
                stepForwardButton: {
                    text: t(V2TranslationKeys.SAVE),
                    onNext: handleFinalSubmit,
                    isDisabled: isSubmitting,
                    isLoading: isSubmitting,
                },
                stepBackButton: {
                    text: t('roles.wizard.back'),
                    onBack: () => setCurrentStep(0),
                },
            },
        ];
        return (React.createElement(WizardModal, { isOpen: isOpen, size: ModalSize.MEDIUM, title: t(V2TranslationKeys.EDIT_USER_TITLE), onDismiss: onClose, denyButton: {
                text: t(V2TranslationKeys.CANCEL),
                onDeny: onClose,
            }, wizardModalStep: wizardSteps, currentStep: currentStep, isProgressBarEnabled: true, includesHeader: true, includesFooter: true, className: createWizardStyles.wizardModal, customHeaderClassName: createWizardStyles.wizardHeader, customStepsClassName: createWizardStyles.wizardContent, customFooterClassName: createWizardStyles.wizardFooter }));
    }
    return (React.createElement(Modal, { isOpen: isOpen, size: ModalSize.MEDIUM, title: t(V2TranslationKeys.EDIT_USER_TITLE), onDismiss: onClose, includesHeader: true, includesFooter: true, isContentLoading: isLoading, confirmButton: {
            text: t(V2TranslationKeys.SAVE),
            onConfirm: handleFinalSubmit,
            isDisabled: isSubmitting || isLoading,
            isLoading: isSubmitting,
        }, dismissButton: {
            text: t(V2TranslationKeys.CANCEL),
            onClick: onClose,
        }, className: styles.editModal, contentClassName: styles.editModalContent },
        React.createElement(StepAssignGroups, { groups: groupItems, groupsLoading: groupsData.loading, selectedGroupIds: selectedGroupIds, onGroupSelectionChange: setSelectedGroupIds, groupSearchText: groupsData.searchText, onGroupSearchChange: groupsData.setSearchText, orgs: orgItems, orgsLoading: false, selectedOrgIds: selectedOrgIds, onOrgSelectionChange: setSelectedOrgIds, isClusterAdmin: false })));
};
//# sourceMappingURL=edit-user-wizard.js.map